import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { userService } from "../../services/api";

const UserApprovalManagement = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("collaborator");
  const [rejectionReason, setRejectionReason] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const roleOptions = [
    { value: "collaborator", label: "Colaborador" },
    { value: "student", label: "Estudiante" },
    { value: "researcher", label: "Investigador" },
  ];

  useEffect(() => {
    loadPendingUsers();
  }, []);

  const loadPendingUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getPendingUsers();
      setPendingUsers(response.data.users);
      setSelectedIds([]);
    } catch (err) {
      setError("Error al cargar usuarios pendientes");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const isAllSelected =
    pendingUsers.length > 0 && selectedIds.length === pendingUsers.length;
  const isIndeterminate =
    selectedIds.length > 0 && selectedIds.length < pendingUsers.length;

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(pendingUsers.map((u) => u.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleBulkApprove = async () => {
    try {
      if (selectedIds.length === 0) return;
      setBulkLoading(true);
      await Promise.allSettled(
        selectedIds.map((id) =>
          userService.approveUser(id, { role: "collaborator" })
        )
      );
      setSuccess(`Aprobados ${selectedIds.length} usuario(s)`);
      setSelectedIds([]);
      loadPendingUsers();
    } catch (err) {
      setError("Error al aprobar usuarios seleccionados");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleApproveUser = async () => {
    try {
      await userService.approveUser(selectedUser.id, { role: selectedRole });
      setSuccess(`Usuario ${selectedUser.name} aprobado exitosamente`);
      setOpenApproveDialog(false);
      setSelectedUser(null);
      loadPendingUsers();
    } catch (err) {
      setError("Error al aprobar usuario");
      console.error(err);
    }
  };

  const handleRejectUser = async () => {
    try {
      await userService.rejectUser(selectedUser.id, { rejectionReason });
      setSuccess(`Usuario ${selectedUser.name} rechazado exitosamente`);
      setOpenRejectDialog(false);
      setSelectedUser(null);
      setRejectionReason("");
      loadPendingUsers();
    } catch (err) {
      setError("Error al rechazar usuario");
      console.error(err);
    }
  };

  const openApproveDialogHandler = (user) => {
    setSelectedUser(user);
    setSelectedRole("collaborator");
    setOpenApproveDialog(true);
  };

  const openRejectDialogHandler = (user) => {
    setSelectedUser(user);
    setRejectionReason("");
    setOpenRejectDialog(true);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h4" component="h1">
              Gestión de Usuarios Pendientes
            </Typography>
            <Box display="flex" gap={2} alignItems="center">
              <Chip
                label={`${pendingUsers.length} Pendientes`}
                color="warning"
                size="large"
              />
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                disabled={selectedIds.length === 0 || bulkLoading}
                onClick={handleBulkApprove}
              >
                {bulkLoading
                  ? "Aprobando..."
                  : `Aprobar seleccionados (${selectedIds.length})`}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {" "}
            {error}{" "}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {" "}
            {success}{" "}
          </Alert>
        )}

        {/* Pending Users Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Fecha de Registro</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(user.id)}
                      onChange={() => toggleSelectOne(user.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PersonIcon color="action" />
                      <Typography variant="subtitle2">{user.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <EmailIcon color="action" />
                      <Typography variant="body2">{user.email}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CalendarIcon color="action" />
                      <Typography variant="body2">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label="Pendiente" color="warning" size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => openApproveDialogHandler(user)}
                      title="Aprobar Usuario"
                      color="success"
                    >
                      <ApproveIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => openRejectDialogHandler(user)}
                      title="Rechazar Usuario"
                      color="error"
                    >
                      <RejectIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {pendingUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No hay usuarios pendientes de aprobación
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Approve User Dialog */}
        <Dialog
          open={openApproveDialog}
          onClose={() => setOpenApproveDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Aprobar Usuario</DialogTitle>
          <DialogContent>
            {selectedUser && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" gutterBottom>
                  ¿Estás seguro de que quieres aprobar a{" "}
                  <strong>{selectedUser.name}</strong>?
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 2 }}
                >
                  Email: {selectedUser.email}
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Asignar Rol</InputLabel>
                  <Select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    label="Asignar Rol"
                  >
                    {roleOptions.map((role) => (
                      <MenuItem key={role.value} value={role.value}>
                        {role.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenApproveDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleApproveUser}
              variant="contained"
              color="success"
              startIcon={<ApproveIcon />}
            >
              Aprobar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject User Dialog */}
        <Dialog
          open={openRejectDialog}
          onClose={() => setOpenRejectDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Rechazar Usuario</DialogTitle>
          <DialogContent>
            {selectedUser && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" gutterBottom>
                  ¿Estás seguro de que quieres rechazar a{" "}
                  <strong>{selectedUser.name}</strong>?
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 2 }}
                >
                  Email: {selectedUser.email}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Motivo del Rechazo (Opcional)"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Proporciona una razón para el rechazo..."
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenRejectDialog(false)}>Cancelar</Button>
            <Button
              onClick={handleRejectUser}
              variant="contained"
              color="error"
              startIcon={<RejectIcon />}
            >
              Rechazar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default UserApprovalManagement;
